import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery, useMutation, gql } from '@zenbusiness/application-commons-graphql';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import Breadcrumbs from '#client/components/Breadcrumbs';
import empty from '#client/utils/empty';
import exists from '#client/utils/exists';
import { getErrorsByField, throwExceptions } from '#client/utils/validation';

const DOMAIN_DNS_MUTATE_READ = gql`
  query DomainDnsRead($domainDnsUuid: UUID!) {
    domainDns(domainDnsUuid: $domainDnsUuid) {
      domainDnsUuid
      type
      dnsConfiguration {
        ipAddress
        subdomain
        hostname
        priority
        text
        port
        weight
        destinationUrl
      }
      deleted
    }
  }
`;

const DOMAIN_DNS_MUTATE_UPDATE = gql`
  mutation DomainDnsUpdate($input: UpdateDomainDnsInput!) {
    updateDomainDns(input: $input) {
      domainDnsUuid
      type
      deleted
      dnsConfiguration {
        ipAddress
        subdomain
        hostname
        priority
        text
        port
        weight
        destinationUrl
      }
    }
  }
`;

const DOMAIN_DNS_MUTATE_DELETE = gql`
  mutation DomainDnsDelete($input: DeleteDomainDnsInput!) {
    deleteDomainDns(input: $input) {
      domainDnsUuid
      type
      deleted
      dnsConfiguration {
        ipAddress
        subdomain
        hostname
        priority
        text
        port
        weight
        destinationUrl
      }
    }
  }
`;

const messages = {
  type: {
    a: 'A',
    cname: 'CNAME',
    mx: 'MX',
    txt: 'TXT',
    srv: 'SRV',
    forward: 'FORWARD'
  },
  validation: {
    generic: 'Error',
    hostname: {
      format: 'Value must be in a valid domain name',
      required: 'Value is required',
      maxLength: 'Value may not be more than 254 characters',
      invalid_hostname: 'Value must be valid'
    },
    ipAddress: {
      format: 'IP Address must be formatted properly',
      required: 'IP Address is required',
      invalid_ip_address: 'IP Address must be formatted properly'
    },
    priority: {
      type: 'Priority must be a number or empty',
      maximum: 'Priority may not be more than 255',
      minimum: 'Priority must be at least 0'
    },
    text: {
      required: 'Text is required',
      minLength: 'Text is required',
      maxLength: 'Text may not be more than 254 characters'
    },
    subdomain: {
      type: 'Hostname must be text',
      maxLength: 'Hostname may not be more than 254 characters',
      max_type_a_record_per_subdomain: 'Only 1 A record allowed per hostname',
      max_type_cname_record_per_subdomain: 'Only 1 CNAME record allowed per hostname',
      max_type_srv_record_per_subdomain: 'Only 1 SRV record allowed per hostname',
      max_type_forward_record_per_subdomain: 'Only 1 FORWARD record allowed per hostname'
    },
    port: {
      type: 'Port must be a number or empty',
      maximum: 'Port may not be more than 65535',
      minimum: 'Port must be at least 0'
    },
    weight: {
      type: 'Weight must be a number or empty',
      maximum: 'Weight may not be more than 255',
      minimum: 'Weight must be at least 0'
    },
    destinationUrl: {
      format: 'Destination must be a valid URL',
      required: 'Destination URL is required',
      maxLength: 'URL may not be more than 254 characters',
      invalid_destination: 'Destination must be in a valid URL format'
    }
  },
  help: {
    common: `For instructions on setting up your website DNS check with your website
      or hosting provider, such as WIX, Squarespace, or Weebly.`,
    a: `An "A" record maps the domain name to its IP address. You can specify an IP
      Address for each A record that you add. An IP address is a 32-bit numeric address
      written as four numbers separated by periods. Each number can be zero to 255. For
      example: 123.123.123.123 could be an IP address.`,
    cname: `A "CNAME" record can only be created for a subdomain and points to another
      domain name. The other domain must be the domain that you want to access with both the
      hostname and domain name.
      For example: www.website.com`,
    mx: `"MX" record or mail exchange record.
      This record is responsible for accepting email messages on behalf of a domain name.
      Priority is the priority of the record; a lower value means more preferred.
      The preference can be any number from 0 to 255 and the default is usually 10.
      If you are entering or updating your MX record please enter the Priority and Value
      exactly as it’s listed in the instructions from your provider. `,
    txt: `Allows you to attach comments to a hostname, for example, SPF data to validate that email
      is being sent from the IP address of the domain's mail server. Enter the comments that you
      want to include in the Text field, to a maximum of 254 characters.
      For Example: This is some text.`,
    srv: `"SRV" (Service) records are custom DNS records.
      They are used to establish connections between a service and a hostname.
      SRV records are used to complete email connections for Microsoft Business email.`,
    forward: `Domain Forwarding or 301 redirect is used to redirect visitors from one URL to another.
      For example You own two domains, mydomain.com and mybiz.com.
      mydomain.com is connected to your existing website,
      but mybiz.com isn’t connected to a website and is just sitting there.
      You decide you want customers who type in mybiz.com to be taken to your existing website
      at mydomain.com so you set up a forward for mybiz.com.
      Forwards are good if you plan on having multiple domain names but one website for all of them.
      It also helps to increase site traffic!`
  },
  config: {
    hostname: 'Value',
    ipAddress: 'IP Address',
    subdomain: 'Hostname',
    text: 'Text',
    priority: 'Priority',
    port: 'Port',
    weight: 'Weight',
    destinationUrl: 'Destination URL'
  }
};

const recordPropsByType = {
  a: [{
    key: 'ipAddress',
    required: true,
    maxLength: 15
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  cname: [{
    key: 'hostname',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  mx: [{
    key: 'hostname',
    required: true,
    maxLength: 254
  }, {
    key: 'priority',
    required: false,
    maxLength: 3
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  txt: [{
    key: 'text',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }],
  srv: [{
    key: 'hostname',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }, {
    key: 'priority',
    required: false,
    maxLength: 3
  }, {
    key: 'port',
    required: true,
    maxLength: 5
  }, {
    key: 'weight',
    required: false,
    maxLength: 3
  }],
  forward: [{
    key: 'destinationUrl',
    required: true,
    maxLength: 254
  }, {
    key: 'subdomain',
    required: false,
    maxLength: 254
  }]
};

const StyledBoxRoot = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2)
}));

const StyledBoxTitleSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const StyledTitlePage = styled(Title)(({ theme }) => ({
  ...theme.typography.title_extraLarge
}));

const StyledTextFieldInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  width: '100%',
  borderRadius: theme.spacing(2),
  '& .MuiInputBase-formControl': {
    borderRadius: theme.spacing(2)
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: theme.spacing(7),
  textTransform: 'capitalize',
  '&:disabled': {
    background: theme.palette.primary.focus30
  }
}));

const StyledForm = styled('form')(() => ({
  width: '100%'
}));

const StyledFormControl = styled(FormControl)(() => ({
  width: '100%'
}));

const StyledTitleHelp = styled(Title)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const StyledGridActions = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const StyledBoxTypeContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse'
  }
}));

const DomainDnsMutate = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid, domainDnsUuid } = useParams();
  const {
    loading: readLoading,
    error: readError,
    data: readData
  } = useQuery(DOMAIN_DNS_MUTATE_READ, {
    noThrow: true,
    variables: {
      domainDnsUuid
    }
  });

  const [updateDns, {
    loading: updateLoading,
    error: updateError,
    data: updateData
  }] = useMutation(DOMAIN_DNS_MUTATE_UPDATE, { noThrow: true });
  const [deleteDns, {
    loading: deleteLoading,
    error: deleteError,
    data: deleteData
  }] = useMutation(DOMAIN_DNS_MUTATE_DELETE, { noThrow: true });

  const loading = readLoading || updateLoading || deleteLoading;

  throwExceptions(readError || updateError || deleteError);

  const updatedDns = updateData && updateData.updateDomainDns;
  const dns = readLoading ? {} : (
    (updatedDns) || (readData && readData.domainDns)
  );

  const [changes, setChanges] = useState({});
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

  // Handle type
  const existingType = ((exists(dns) && exists(dns.type)) ? dns.type : null);
  const changedType = (exists(changes.type) ? changes.type : null);
  const type = exists(changedType) ? changedType : existingType;

  const handleDeleteDialogOpen = () => {
    setDeleteDialogVisible(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogVisible(false);
  };

  const handleDnsDelete = () => {
    if (!loading) {
      deleteDns({
        variables: {
          input: {
            domainDnsUuid
          }
        }
      });
      handleDeleteDialogClose();
    }
  };

  const handleRecordChange = (event) => {
    const {
      name,
      value
    } = event.target;

    setChanges({
      ...changes,
      [name]: value
    });
  };

  const unsaved = !empty(changes);

  const handleDnsSave = () => {
    if (!loading && unsaved) {
      const { type: newType, ...newDnsRecord } = changes;
      const data = {
        dnsConfiguration: newDnsRecord
      };
      updateDns({
        variables: {
          input: {
            domainDnsUuid,
            data
          }
        }
      });
    }
  };

  if (!loading && (dns.deleted || deleteData || updatedDns)) {
    // Navigate post deletion
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}#dns`} />);
  }

  // Process server validation errors
  const errorsByField = getErrorsByField(updateError);

  // Handle dynamic record configuration fields
  const records = [];

  if (type !== null) {
    recordPropsByType[type].forEach(({ key, required, maxLength }) => {
      const existingValue = ((exists(dns) && exists(dns.dnsConfiguration) && exists(dns.dnsConfiguration[key])) ? dns.dnsConfiguration[key] : '');
      const changedValue = (exists(changes[key]) ? changes[key] : null);
      const errorKey = exists(errorsByField[key]) && errorsByField[key][0];
      const errorMessage = exists(errorsByField[key]) && errorsByField[key][1];
      let error = null;
      if (messages.validation[key] && errorKey) {
        error = messages.validation[key][errorKey] || errorMessage || messages.validation.generic;
      }

      records.push({
        key,
        value: exists(changedValue) ? changedValue : existingValue,
        required,
        error,
        maxLength
      });
    });
  }

  return (
    <StyledBoxRoot>
      <Breadcrumbs gutterBottom>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools`}>Overview</Button>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}>Domains</Button>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>Domain</Button>
        <Button variant="text" disabled color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/dns/${domainDnsUuid}`}>DNS Record</Button>
      </Breadcrumbs>

      <StyledBoxTitleSection>
        <StyledTitlePage
          variant="md-alt"
          level={2}
        >
          Edit a DNS Record
        </StyledTitlePage>
      </StyledBoxTitleSection>

      <div>
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={10}
        >
          <Grid container item xs={12} md={8}>
            <StyledForm noValidate autoComplete="off">
              <StyledBoxTypeContainer>
                <Text
                  variant="sm"
                  color="focus50"
                >
                  Type
                </Text>
                <Title
                  level={3}
                  variant="sm"
                >
                  {messages.type[type]}
                </Title>
              </StyledBoxTypeContainer>

              <StyledFormControl component="fieldset">
                {records.map(({
                  key, value, required, error, maxLength
                }) => (
                  <StyledTextFieldInput
                    key={key}
                    id={key}
                    variant="outlined"
                    size="medium"
                    label={messages.config[key]}
                    name={key}
                    value={value}
                    onChange={handleRecordChange}
                    helperText={error}
                    error={!!error}
                    required={required}
                    inputProps={{
                      maxLength
                    }}
                  />
                ))}
              </StyledFormControl>

              <StyledGridActions
                container
                spacing={3}
              >
                <Grid item xs={12} md={6}>
                  <StyledButton variant="contained" color="primary" onClick={handleDnsSave} disabled={loading || !unsaved}>save</StyledButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledButton variant="outlined" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>cancel</StyledButton>
                </Grid>
              </StyledGridActions>
              <StyledGridActions
                container
                spacing={3}
              >
                <Grid item xs={12}>
                  <StyledButton variant="outlined" color="secondary" onClick={handleDeleteDialogOpen} disabled={loading}>delete record</StyledButton>
                </Grid>
              </StyledGridActions>
            </StyledForm>
          </Grid>
          {type && (
            <Grid item xs={12} md={8}>
              <StyledTitleHelp
                variant="sm"
                level={4}
              >
                {`${messages.type[type]} Record Help`}
              </StyledTitleHelp>
              <Text size="md" gutterBottom>
                {messages.help.common}
              </Text>
              <Text size="md">
                {messages.help[type]}
              </Text>
            </Grid>
          )}
        </Grid>
      </div>

      <Dialog
        open={deleteDialogVisible}
        onClose={handleDeleteDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>
          Delete this DNS record?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This record will be removed permanently.
            Any behavior dependent on this record will cease to work.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary" autoFocus>
            Cancel
          </Button>
          <Button onClick={handleDnsDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </StyledBoxRoot>
  );
};

export default DomainDnsMutate;
